<template>
  <div :class="['comment-item', { 'is-reply': isReply }]">
    <div class="comment-header">
      <div class="comment-author">
        <div class="user-display-content">
          <div class="user-username">
            <template v-if="comment.author?.username">@{{ comment.author.username }}</template>
            <template v-else-if="comment.authorName">{{ comment.authorName }}</template>
            <template v-else>—</template>
          </div>
          <div v-if="comment.author?.name" class="user-name">{{ comment.author.name }}</div>
          <div v-if="authorProjectRole" class="user-project-role">
            {{ authorProjectRole }}
          </div>
        </div>
        <span v-if="comment.edited" class="edited-badge" :title="s__('TaskDetail|Edited')">
          <EditIcon :size="12" />
        </span>
      </div>
      <span class="comment-time">
        <formatted-date-time :value="comment.createdAt" :locale="locale" :timezone="timezone" />
      </span>
    </div>
    <div
      class="comment-body rich-content-read"
      @click="handleTaskMentionClick"
      v-html="processedCommentBody"
    ></div>
    <div
      v-if="s3AttachmentsEnabled && comment.attachments && comment.attachments.length > 0"
      class="comment-footer"
    >
      <hr class="comment-footer-separator" />
      <comment-attachments
        :attachments="comment.attachments"
        :can-delete="comment.canDelete"
        @delete="handleDeleteAttachment"
      />
    </div>
    <div v-if="!isEditing" class="comment-actions">
      <button
        v-if="canComment && !isReply"
        class="comment-action-btn"
        @click="$emit('reply', comment)"
      >
        {{ s__('TaskDetail|Reply') }}
      </button>
      <button v-if="comment.canUpdate" class="comment-action-btn" @click="$emit('edit', comment)">
        {{ s__('TaskDetail|Edit') }}
      </button>
      <button
        v-if="comment.canDelete"
        class="comment-action-btn comment-action-delete"
        @click="$emit('delete', comment)"
      >
        {{ s__('TaskDetail|Delete') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { telegramMarkdownToHtml } from '../../../utils/telegramEditorCodec.js'
import { getProjectRole } from '@/utils/projectRoles'
import FormattedDateTime from '../../common/FormattedDateTime.vue'
import EditIcon from '../../common/icons/EditIcon.vue'
import CommentAttachments from '../../common/CommentAttachments.vue'

const props = defineProps({
  comment: { type: Object, required: true },
  projectRolesMap: { type: Map, default: () => new Map() },
  canComment: { type: Boolean, default: false },
  currentUserId: { type: String, default: null },
  isReply: { type: Boolean, default: false },
  isEditing: { type: Boolean, default: false },
  projectId: { type: String, default: null },
  taskMentionsMap: { type: Object, default: () => ({}) },
  locale: { type: String, default: null },
  timezone: { type: String, default: null },
})

const emit = defineEmits(['reply', 'edit', 'delete', 'delete-attachment'])

// Feature flag for S3 attachments
const s3AttachmentsEnabled = computed(() => {
  return window.gon?.s3_attachments_enabled === true
})

const handleDeleteAttachment = attachment => {
  emit('delete-attachment', attachment)
}

// Build task URL helper
const buildTaskUrl = (taskId, taskProjectId) => {
  const chatId = new URLSearchParams(window.location.search).get('chat_id')
  const fromId = new URLSearchParams(window.location.search).get('from_id')

  let url = `/projects/${taskProjectId || props.projectId}/tasks/${taskId}`
  const params = []
  if (chatId) params.push(`chat_id=${encodeURIComponent(chatId)}`)
  if (fromId) params.push(`from_id=${encodeURIComponent(fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')

  return url
}

// Process comment body with task mentions
const processedCommentBody = computed(() => {
  if (!props.comment?.body) return ''

  return telegramMarkdownToHtml(props.comment.body, {
    processTaskMentions: true,
    taskMap: props.taskMentionsMap || {}, // Use provided task mentions map
    buildTaskUrl: (taskId, projectId) => buildTaskUrl(taskId, projectId || props.projectId),
  })
})

// Computed: project role for comment author
const authorProjectRole = computed(() => {
  if (!props.comment?.author?.id) return null
  return getProjectRole(props.projectRolesMap, props.comment.author.id)
})

// Handle clicks on task mention links
const handleTaskMentionClick = event => {
  if (event.target.classList.contains('task-mention-link')) {
    event.preventDefault()
    const taskId = event.target.dataset.taskId
    const projectId = event.target.dataset.projectId || props.projectId
    const url = buildTaskUrl(taskId, projectId)
    window.location.href = url
  }
}
</script>

<style scoped>
.comment-item {
  padding: 12px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  transition: box-shadow 0.2s;
}

.comment-item:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.comment-item.is-reply {
  background: #fafafa;
}

.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.comment-author {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  color: #424242;
  font-size: 14px;
}

.user-display-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-username {
  font-weight: 500;
  color: #212529;
  font-size: 14px;
}

.user-name {
  font-size: 12px;
  color: #9e9e9e;
  font-weight: 400;
}

.user-project-role {
  font-size: 11px;
  color: #9e9e9e;
  font-style: italic;
}

.edited-badge {
  font-size: 11px;
  opacity: 0.6;
}

.comment-time {
  font-size: 12px;
  color: #757575;
}

.comment-body {
  color: #424242;
  line-height: 1.5;
  margin-bottom: 8px;
  word-wrap: break-word;
}

.comment-actions {
  display: flex;
  gap: 12px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}

.comment-action-btn {
  padding: 4px 8px;
  font-size: 12px;
  border: none;
  background: transparent;
  color: #2196f3;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
  border-radius: 2px;
}

.comment-action-btn:hover {
  background: #e3f2fd;
  color: #1976d2;
}

.comment-action-delete {
  color: #d32f2f;
}

.comment-action-delete:hover {
  background: #ffebee;
  color: #c62828;
}

.comment-footer {
  margin-top: 8px;
  padding-top: 8px;
}

.comment-footer-separator {
  margin: 0 0 8px 0;
  border: none;
  border-top: 1px solid #f0f0f0;
}
</style>
